import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  ClickAwayListener,
  Grid,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { COLOR_ACCENT_1, COLOR_PRIMARY, exchangeRates, userDisplayFriendlyCountryCode } from 'src/const';
import { Link, useHistory } from 'react-router-dom';
import {
  getScoreColorAndValue,
  marketInSampleFreeMarkets,
  processCompareData,
} from 'src/App/Pages/OverviewPage/helper';
import {
  abbreviateNumber,
  convertCurrency,
  getCountryDataFromCountryCode,
} from 'src/utils/functions';
import { COLOR_GREY_600, COLOR_PRIMARY_1 } from 'src/colors';
import Checkbox from '@mui/material/Checkbox';
import { InfoTooltip } from '../Tooltips';
import TooltipCard from '../Cards/TooltipCard';
import { Tooltip, makeStyles } from '@material-ui/core';
import ScoreTooltip from 'src/App/Pages/OverviewPage/UtilityComponents/ScoreTooltip';
import CustomScoreCardTooltip from 'src/App/Pages/OverviewPage/UtilityComponents/CustomScoreCardTooltip';

const useStylesRoot = makeStyles({
  tooltip: {
    padding: 0,
    backgroundColor: 'transparent !important',
    margin: '0px !important',
  },
  popperInteractive: {
    width: '370px !important',
  },
  tooltipIcon: {
    position: 'relative',
    width: '100px',
    height: '30px',
    background: '#fff',
    color: '#000',
    borderRadius: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
    transform: 'translate(-50%, -50%)',
    transition: 'background 0.3s ease, color 0.3s ease',
    '&:hover': {
      background: '#000',
      color: '#fff',
    },
  },
});

const MarketPointer = ({
  id,
  lat,
  lng,
  pointSize,
  name,
  score,
  countryCode,
  isHighlighted,
  data,
  marketsToCompareList,
  setMarketsToCompareList,
  dashboardLayoutView,
  minROI,
  maxROI,
  isMarketDataAccessible,
  currency,
  freeMarketDataKey,
  handleOnSampleClick,
  marketLength,
}: {
  id: number;
  lat: number;
  lng: number;
  pointSize: number;
  countryCode: string;
  name: string;
  score: number;
  isHighlighted?: boolean;
  data: any;
  marketsToCompareList: any[];
  setMarketsToCompareList: any;
  dashboardLayoutView: string;
  minROI: number;
  maxROI: number;
  isMarketDataAccessible: boolean;
  currency: string;
  freeMarketDataKey: string[];
  handleOnSampleClick: any;
  marketLength: number;
}) => {
  const matches = useMediaQuery('(max-width:550px)');
  const [isChipHighlighted, setIsChipHighlighted] = useState<boolean>(
    isHighlighted ? isHighlighted : false
  );
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const rootClass = useStylesRoot();

  const history = useHistory();
  const [openTooltip, setOpenTooltip] = useState(null);

  const handleTooltipOpen = tooltipName => {
    setOpenTooltip(tooltipName);
  };
  const handleTooltipClose = () => {
    setOpenTooltip(null);
  };

  const redirectToTargetMarket = () => {
    const country = data.country? data.country.toLowerCase().replace(" ", "-"): '0';
    const url = `/airbnb-data/${country}/${data.city.toLowerCase()}/${data.district.toLowerCase()}`;
    window.open(url, '_blank');
  };

  const handleCheckBoxSelection = e => {
    if (e.target.checked === true) {
      if (marketsToCompareList.length < 4) {
        setMarketsToCompareList([
          ...marketsToCompareList,
          processCompareData(data, isMarketDataAccessible, freeMarketDataKey, currency),
        ]);
      }
    } else {
      setMarketsToCompareList(_ =>
        [...marketsToCompareList].filter(currentMarket => currentMarket[0] !== data.district)
      );
    }
  };

  useEffect(() => {
    if (dashboardLayoutView === 'compare') {
      if (isHighlighted) {
        setIsChipHighlighted(false);
      }
      for (let market of marketsToCompareList) {
        if (market[0] === data.district) {
          setIsChipHighlighted(true);
        }
      }
    } else {
      if (!isHighlighted) {
        for (let market of marketsToCompareList) {
          setIsChipHighlighted(false);
        }
      }
    }
  }, [marketsToCompareList, dashboardLayoutView]);

  const checkIfCheckboxIsSelected = () => {
    let isChecked = false;
    for (let selectedMarket of marketsToCompareList) {
      if (selectedMarket[0] === data.district) {
        isChecked = true;
        break;
      }
    }
    return isChecked;
  };

  const icon = (
    <Box
      className="discovery-score-card"
      onClick={redirectToTargetMarket}
      sx={{
        position: 'relative',
        minWidth: '50px',
        width: '50vw',
        maxWidth: '135px',
        height: '35px',
        background: isChipHighlighted ? '#000' : '#fff',
        color: isChipHighlighted ? '#fff' : '#000',
        borderRadius: 100,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
        transform: 'translate(-50%, -50%)',
        transition: 'background 0.3s ease, color 0.3s ease',
        overflow: 'clip',
        '&:hover': {
          background: isChipHighlighted ? '#fff' : '#000',
          color: isChipHighlighted ? '#000' : '#fff',
        },
      }}
    >
      <Box sx={{ padding: '5px' }}>
        <Box sx={{ position: 'relative', display: 'inline-flex', left: '2px' }}>
          <CircularProgress
            variant="determinate"
            value={getScoreColorAndValue(score, maxROI, minROI).score}
            size={32}
            thickness={3}
            sx={{
              '& .MuiCircularProgress-circle': {
                strokeLinecap: 'round',
              },
              color: getScoreColorAndValue(score, maxROI, minROI).color,
              marginTop: '3px',
            }}
          />
          <Box
            sx={{
              top: '4px',
              left: 0,
              bottom: 0,
              right: 0,
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography sx={{ fontSize: '10px', fontWeight: 500 }}>{score.toFixed(1)}x</Typography>
          </Box>
        </Box>
      </Box>
      <pre
        style={{
          paddingRight: '2px',
          textOverflow: 'ellipsis',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            minWidth: '40px',
            width: '50vw',
            maxWidth: '100px',
            overflow: 'clip',
            textOverflow: 'ellipsis',
          }}
        >
          {name}
        </Typography>
      </pre>
    </Box>
  );

  return (
    <>
      <ClickAwayListener onClickAway={() => setTooltipOpen(false)}>
        <Tooltip
          interactive
          classes={rootClass}
          open={tooltipOpen}
          enterDelay={0}
          enterTouchDelay={0}
          onOpen={() => {
            setTooltipOpen(true);
          }}
          onClose={() => {
            setTooltipOpen(false);
          }}
          style={{ zIndex: 999999 }}
          title={
            <Card sx={{ width: '370px' }}>
              <CardContent style={{ zIndex: 999999 }}>
                <Grid container>
                  <Grid
                    item
                    sx={{
                      height: '25px',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Tooltip
                      arrow
                      title={
                        marketsToCompareList.length >= 4
                          ? 'Maximum Comparison Limit of Four Markets has been reached. Please remove a market to add more.'
                          : 'Add to compare.'
                      }
                    >
                      <Checkbox
                        sx={{ padding: 0 }}
                        checked={checkIfCheckboxIsSelected()}
                        onChange={handleCheckBoxSelection}
                        disabled={marketsToCompareList.length >= 4 && !checkIfCheckboxIsSelected()}
                      />
                    </Tooltip>
                  </Grid>
                  <Grid item xs={9} sx={{ display: 'flex', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {' '}
                      {data.district},
                    </Typography>
                    <span
                      style={{
                        color: COLOR_GREY_600,
                        fontFamily: 'Roboto',
                        fontSize: '15px',
                        paddingTop: '5px',
                      }}
                    >
                      {' '}
                      {/* {getCountryDataFromCountryCode(countryCode).label} */}
                      {!data.city || data.city == '' || data.city == 0 ? userDisplayFriendlyCountryCode[data.country_code] :data. city}
                    </span>
                    <Box
                      sx={{ height: '20px', width: '30px', overflow: 'hidden', marginTop: '2px' }}
                    >
                      <img
                        src={`https://flagcdn.com/w80/${data.country_code?.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w80/${data.country_code?.toLowerCase()}.png 2x`}
                        alt="Country flag"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  </Grid>
                  {/* Score */}
                  <Grid
                    item
                    xs={3}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  >
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        interactive
                        classes={rootClass}
                        open={openTooltip == 'score'}
                        onOpen={() => {
                          handleTooltipOpen('score');
                        }}
                        enterDelay={0}
                        enterTouchDelay={0}
                        onClose={handleTooltipClose}
                        title={
                          <Card
                            sx={{
                              height: '370px',
                              width: '330px',
                              padding: '10px !important',
                              position: 'relative',
                            }}
                          >
                            <CardContent
                              sx={{
                                zIndex: 999999,
                                height: '100%',
                                width: '100%',
                                padding: '0px',
                              }}
                            >
                              <ScoreTooltip market={data} currency={currency} />
                            </CardContent>
                          </Card>
                        }
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'inline-flex',
                            '&:hover': { cursor: 'pointer' },
                          }}
                        >
                          <CircularProgress
                            variant="determinate"
                            value={getScoreColorAndValue(score, maxROI, minROI).score}
                            thickness={5}
                            size={40}
                            sx={{
                              '& .MuiCircularProgress-circle': {
                                strokeLinecap: 'round',
                              },
                              color: getScoreColorAndValue(score, maxROI, minROI).color,
                            }}
                          />
                          <Box
                            sx={{
                              top: 0,
                              left: 0,
                              bottom: 0,
                              right: 0,
                              position: 'absolute',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                              {score.toFixed(1)}x
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    </ClickAwayListener>
                  </Grid>
                </Grid>

                {/* <Box sx={{ height: '85px' }}> */}

                <Grid
                  container
                  sx={{ height: '100%', backgroundColor: '#f8f8f8', borderRadius: '20px' }}
                >
                  {/* Yearly revenue */}
                  <Grid item sm={4} sx={{ height: '100%', padding: '15px 5px 0 10px' }}>
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        interactive
                        classes={rootClass}
                        open={openTooltip == 'revenue'}
                        onOpen={() => {
                          handleTooltipOpen('revenue');
                        }}
                        onClose={handleTooltipClose}
                        enterDelay={0}
                        enterTouchDelay={0}
                        title={
                          <Card
                            sx={{ height: '150px', width: '370px', padding: '5px 10px !important' }}
                          >
                            <CardContent
                              style={{
                                zIndex: 999999,
                                height: '100%',
                                width: '100%',
                                padding: '0px',
                              }}
                            >
                              <CustomScoreCardTooltip
                                isMarketDataAccessible={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                }
                                marketName={data.district}
                                col1Title="Regulation"
                                col1Val={'Coming Soon'}                                
                                col2Title="Occupancy Rate"
                                col2Val={data.occupancy_rate + '%'}
                                col3Title="Daily Rate"
                                col3Val={
                                  exchangeRates[currency].symbol +
                                  abbreviateNumber(convertCurrency(currency, data.adr))
                                }
                                handleOnSampleClick={handleOnSampleClick}
                                countryCode={countryCode}
                              />
                            </CardContent>
                          </Card>
                        }
                      >
                        <Box
                          sx={{
                            color: openTooltip == 'revenue' ? COLOR_PRIMARY_1 : '#000',
                            '&:hover': { cursor: 'pointer' },
                          }}
                        >
                          <Typography variant="body1" sx={{ fontWeight: 500, textAlign: 'center' }}>
                            {exchangeRates[currency].symbol +
                              abbreviateNumber(convertCurrency(currency, data.revenue))}
                          </Typography>
                          <Typography style={{ fontSize: '12px', textAlign: 'center' }}>
                            Yearly Revenue
                          </Typography>
                        </Box>
                      </Tooltip>
                    </ClickAwayListener>
                  </Grid>
                  {/* Net Rental Yield */}
                  <Grid
                    item
                    sm={4}
                    sx={{
                      height: '100%',
                      borderLeft: '1px solid grey',
                      borderRight: '1px solid grey',
                      padding: '15px 5px 0 10px',
                    }}
                  >
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        interactive
                        classes={rootClass}
                        open={openTooltip == 'yield'}
                        onOpen={() => {
                          handleTooltipOpen('yield');
                        }}
                        onClose={handleTooltipClose}
                        enterDelay={0}
                        enterTouchDelay={0}
                        title={
                          <Card
                            sx={{ height: '150px', width: '370px', padding: '5px 10px !important' }}
                          >
                            <CardContent
                              style={{
                                zIndex: 999999,
                                height: '100%',
                                width: '100%',
                                padding: '0px',
                              }}
                            >
                              <CustomScoreCardTooltip
                                isMarketDataAccessible={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                }
                                marketName={data.district}
                                col1Title="Property Price"
                                col1Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '$ 100k'
                                    : exchangeRates[currency].symbol +
                                      abbreviateNumber(
                                        convertCurrency(currency, data.property_price)
                                      )
                                }
                                col2Title="Average Revenue"
                                col2Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '$ 100k'
                                    : exchangeRates[currency].symbol +
                                      abbreviateNumber(convertCurrency(currency, data.revenue))
                                }
                                col3Title="Average Expenses"
                                col3Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '$ 100k'
                                    : exchangeRates[currency].symbol +
                                      abbreviateNumber(
                                        convertCurrency(currency, data.average_expense)
                                      )
                                }
                                handleOnSampleClick={handleOnSampleClick}
                                countryCode={countryCode}
                              />
                            </CardContent>
                          </Card>
                        }
                      >
                        <Box
                          sx={{
                            color: openTooltip == 'yield' ? COLOR_PRIMARY_1 : '#000',
                            '&:hover': { cursor: 'pointer' },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              textAlign: 'center',
                              filter:
                                !isMarketDataAccessible &&
                                !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                  ? 'blur(3.5px) '
                                  : 'blur(0px)',
                            }}
                            onClick={
                              !isMarketDataAccessible &&
                              !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                ? handleOnSampleClick
                                : null
                            }
                          >
                            {!isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(data, freeMarketDataKey)
                              ? '100'
                              : data.net_rental_yield}{' '}
                            %
                          </Typography>
                          <Typography style={{ fontSize: '12px', textAlign: 'center' }}>
                            Net Rental Yield
                          </Typography>
                        </Box>
                      </Tooltip>
                    </ClickAwayListener>
                  </Grid>
                  {/* Annual Appreciation */}
                  <Grid item sm={4} sx={{ height: '100%', padding: '15px 0 0 10px' }}>
                    <ClickAwayListener onClickAway={handleTooltipClose}>
                      <Tooltip
                        interactive
                        classes={rootClass}
                        open={openTooltip === 'annualAppreciation'}
                        onOpen={() => {
                          handleTooltipOpen('annualAppreciation');
                        }}
                        enterDelay={0}
                        enterTouchDelay={0}
                        onClose={handleTooltipClose}
                        title={
                          <Card
                            sx={{ height: '160px', width: '370px', padding: '5px 10px !important' }}
                          >
                            <CardContent
                              style={{
                                zIndex: 999999,
                                height: '100%',
                                width: '100%',
                                padding: '0px',
                              }}
                            >
                              <CustomScoreCardTooltip
                                isMarketDataAccessible={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                }
                                marketName={data.district}
                                col1Title="Property Price Change"
                                col1Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '100%'
                                    : data.property_price_change + '%'
                                }
                                col1TooltipObj={{
                                  rank: data.property_price_change_rank,
                                  totalMarkets: marketLength,
                                  nationalAverageComparision: data.prop_price_change_diff_with_nat_avg,
                                }}
                                col2Title="Polulation Change"
                                col2Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '100%'
                                    : data.population_change + '%'
                                }
                                col2TooltipObj={{
                                  rank: data.population_change_rank,
                                  totalMarkets: marketLength,
                                  nationalAverageComparision: data.population_change_diff_with_nat_avg,
                                }}
                                col3Title="Income Change"
                                col3Val={
                                  !isMarketDataAccessible &&
                                  !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                    ? '100%'
                                    : data.income_change + '%'
                                }
                                col3TooltipObj={{
                                  rank: data.income_change_rank,
                                  totalMarkets: marketLength,
                                  nationalAverageComparision: data.income_change_diff_with_nat_avg,
                                }}
                                handleOnSampleClick={handleOnSampleClick}
                                countryCode={countryCode}
                              />
                            </CardContent>
                          </Card>
                        }
                      >
                        <Box
                          sx={{
                            color: openTooltip === 'annualAppreciation' ? COLOR_PRIMARY_1 : '#000',
                            '&:hover': { cursor: 'pointer' },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 500,
                              textAlign: 'center',
                              filter:
                                !isMarketDataAccessible &&
                                !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                  ? 'blur(3.5px) '
                                  : 'blur(0px)',
                            }}
                            onClick={
                              !isMarketDataAccessible &&
                              !marketInSampleFreeMarkets(data, freeMarketDataKey)
                                ? handleOnSampleClick
                                : null
                            }
                          >
                            {!isMarketDataAccessible &&
                            !marketInSampleFreeMarkets(data, freeMarketDataKey)
                              ? '100'
                              : data.annual_appreciation}
                            %
                          </Typography>
                          <Typography style={{ fontSize: '12px', textAlign: 'center' }}>
                            Annual Appreciation
                          </Typography>
                        </Box>
                      </Tooltip>
                    </ClickAwayListener>
                  </Grid>
                </Grid>
                {/* </Box> */}
                <Button
                  sx={{
                    width: '100%',
                    marginTop: '10px',
                    background: COLOR_PRIMARY + '22',
                    color: COLOR_PRIMARY,
                    borderRadius: '4px',
                    '&:hover': {
                      color: '#fff',
                      background: COLOR_PRIMARY,
                    },
                  }}
                  onClick={redirectToTargetMarket}
                >
                  <Typography>View Market</Typography>
                </Button>
              </CardContent>
            </Card>
          }
        >
          {icon}
        </Tooltip>
      </ClickAwayListener>
    </>
  );
};

export default MarketPointer;
